import React, {
  memo,
  type ReactElement,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  ButtonIcon,
  ButtonSelect,
  Icon,
  InputField,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import { SodApplicationGroup } from '../../../shared/components';
import SodColumnsList from '../../../shared/components/SodColumnsList';
import {
  CF_NEU_BLD,
  CF_NEU_STL,
  type EntitlementRowType,
} from '../../../shared/constants';
import { useInvertedLocalize, useTypedSelector } from '../../../shared/hooks';
import { globalActions } from '../../../shared/store/actions';
import { testIds } from '../../../tests';

import SodEntitlementListGroup from './SodEntitlementListGroup/SodEntitlementListGroup';
import SodEntitlementSidePanel from './SodEntitlementSidePanel/SodEntitlementSidePanel';

import './ViolationsEntitlement.css';

const ACTIVE_ASIDE_OPTIONS = {
  SIDE_PANEL: 'side-panel',
  INSIGHTS: 'insights',
};

const TEMP_LIST_ITEMS_MOCK: EntitlementRowType[] = [
  {
    id: 1,
    title: 'Finance Admin Access',
    role: 'FinanceAdmin',
    applicationComp: (
      <SodApplicationGroup
        label='Oracle EBS'
        firstLogo='Oracle'
        secondLogo='SAP'
        count={4}
      />
    ),
    description: 'Access to admin-level financial tools',
    inherentRisks: 20,
    users: 12,
    violations: 104,
    entitlementType: 'Role-Based',
    soxCritical: 'Very High',
    sysCritical: 'High',
    risk: 'Very High',
    privileged: 'Very High',
    financial: 'Very High',
    updateDate: '01-06-2024 14:21:01',
  },
  {
    id: 2,
    title: 'HR Records Viewer',
    role: 'HRViewer',
    applicationComp: <SodApplicationGroup label='SAP ECC' firstLogo='SAP' />,
    description: 'View-only access to HR records',
    inherentRisks: 5,
    users: 47,
    violations: 12,
    entitlementType: 'Read-Only',
    soxCritical: 'Low',
    sysCritical: 'Low',
    risk: 'Low',
    privileged: 'None',
    financial: 'None',
    updateDate: '01-06-2024 14:21:01',
  },
  {
    id: 3,
    title: 'Payroll Processor',
    role: 'PayrollExec',
    applicationComp: <SodApplicationGroup label='SAP ECC' firstLogo='SAP' />,
    description: 'Edit and execute payroll transactions',
    inherentRisks: 30,
    users: 9,
    violations: 76,
    entitlementType: 'Transactional',
    soxCritical: 'High',
    sysCritical: 'High',
    risk: 'High',
    privileged: 'High',
    financial: 'Very High',
    updateDate: '01-06-2024 14:21:01',
  },
  {
    id: 4,
    title: 'Customer Data Admin',
    role: 'CustDataAdmin',
    applicationComp: <SodApplicationGroup label='SAP ECC' firstLogo='SAP' />,
    description: 'Full access to customer personal information',
    inherentRisks: 50,
    users: 5,
    violations: 142,
    entitlementType: 'Admin',
    soxCritical: 'High',
    sysCritical: 'Very High',
    risk: 'Very High',
    privileged: 'Very High',
    financial: 'High',
    updateDate: '01-06-2024 14:21:01',
  },
  {
    id: 5,
    title: 'IT Support Level 1',
    role: 'ITSupportL1',
    applicationComp: <SodApplicationGroup label='SAP ECC' firstLogo='SAP' />,
    description: 'Basic IT troubleshooting access',
    inherentRisks: 8,
    users: 38,
    violations: 6,
    entitlementType: 'Support',
    soxCritical: 'Low',
    sysCritical: 'Low',
    risk: 'Low',
    privileged: 'Low',
    financial: 'None',
    updateDate: '01-06-2024 14:21:01',
  },
  {
    id: 6,
    title: 'Marketing Campaign Manager',
    role: 'MktgMgr',
    applicationComp: (
      <SodApplicationGroup
        label='Workday'
        firstLogo='EPIC'
        secondLogo='Beyondtrust'
        count={2}
      />
    ),
    description: 'Manage digital marketing campaigns and assets',
    inherentRisks: 12,
    users: 18,
    violations: 28,
    entitlementType: 'Operational',
    soxCritical: 'None',
    sysCritical: 'Low',
    risk: 'Low',
    privileged: 'None',
    financial: 'Low',
    updateDate: '01-06-2024 14:21:01',
  },
  {
    id: 7,
    title: 'Cloud Resource Admin',
    role: 'CloudAdmin',
    applicationComp: <SodApplicationGroup label='SAP ECC' firstLogo='SAP' />,
    description: 'Admin access to cloud infrastructure',
    inherentRisks: 40,
    users: 6,
    violations: 188,
    entitlementType: 'Admin',
    soxCritical: 'High',
    sysCritical: 'Very High',
    risk: 'Very High',
    privileged: 'Very High',
    financial: 'None',
    updateDate: '01-06-2024 14:21:01',
  },
  {
    id: 8,
    title: 'Procurement Access',
    role: 'ProcureUser',
    applicationComp: (
      <SodApplicationGroup
        label='Workday'
        firstLogo='Workday'
        secondLogo='Slack'
        count={10}
      />
    ),
    description: 'Create and approve purchase orders',
    inherentRisks: 18,
    users: 21,
    violations: 39,
    entitlementType: 'Transactional',
    soxCritical: 'High',
    sysCritical: 'Medium',
    risk: 'High',
    privileged: 'Low',
    financial: 'High',
    updateDate: '01-06-2024 14:21:01',
  },
  {
    id: 9,
    title: 'Legal Document Viewer',
    role: 'LegalViewer',
    applicationComp: <SodApplicationGroup label='SAP ECC' firstLogo='SAP' />,
    description: 'Read-only access to legal documents',
    inherentRisks: 6,
    users: 11,
    violations: 4,
    entitlementType: 'Read-Only',
    soxCritical: 'None',
    sysCritical: 'Low',
    risk: 'Low',
    privileged: 'None',
    financial: 'None',
    updateDate: '01-06-2024 14:21:01',
  },
  {
    id: 10,
    title: 'Engineering Source Control',
    role: 'DevOpsSC',
    applicationComp: <SodApplicationGroup label='SAP ECC' firstLogo='SAP' />,
    description: 'Access to source control repositories',
    inherentRisks: 25,
    users: 15,
    violations: 97,
    entitlementType: 'Developer',
    soxCritical: 'Low',
    sysCritical: 'High',
    risk: 'High',
    privileged: 'High',
    financial: 'None',
    updateDate: '01-06-2024 14:21:01',
  },
];

/**
 * * TODO
 * - Write a description
 * - This Page needs to set Scroll area width if the SideNav is pinned open or not using redux user: isMenuPinned
 * - Get Search Placeholder text from design
 * - What else should Search input string filter???
 * - Added relevent data to redux slices
 * - Localize these labels
 */
function ViolationsEntitlement(): ReactElement | null {
  // Search
  const [searchValue, setSearchValue] = useState('');
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  // Asides
  const [activeAside, setActiveAside] = useState(ACTIVE_ASIDE_OPTIONS.INSIGHTS);
  const [isInsightsOpen, setIsInsightsOpen] = useState(false);
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<EntitlementRowType | null>(
    null
  );

  const dispatch = useDispatch();
  const l = useInvertedLocalize();

  const { isMenuPinned } = useTypedSelector((state) => state.user);

  // Demo setting global state to false after 1s
  useEffect(() => {
    setTimeout(() => {
      dispatch(globalActions.setIsGlobalLoading(false));
    }, 1000);
  }, [dispatch]);

  const entitlementClasses = cx(
    'Sod-ViolationsEntitlement',
    isMenuPinned && 'Sod-ViolationsEntitlement--isMenuPinned'
  );
  const listAsideClasses = cx(
    'Sod-ViolationsEntitlement-main',
    isInsightsOpen && 'Sod-ViolationsEntitlement-main--isInsightsOpen',
    isSidePanelOpen && 'Sod-ViolationsEntitlement-main--isSidePanelOpen'
  );

  const handleCloseSidePanel = () => {
    setIsSidePanelOpen(false);
    setSelectedRow(null);
  };

  const handleOpenSidePanel = (row: EntitlementRowType) => {
    setSelectedRow(row);
    setIsSidePanelOpen(true);
    setIsInsightsOpen(false);
    setActiveAside(ACTIVE_ASIDE_OPTIONS.SIDE_PANEL);
  };

  const handleOpenInsights = () => {
    setSelectedRow(null);
    setIsInsightsOpen(true);
    setIsSidePanelOpen(false);
    setActiveAside(ACTIVE_ASIDE_OPTIONS.INSIGHTS);
  };

  const filteredListRows: EntitlementRowType[] = useMemo(
    () =>
      TEMP_LIST_ITEMS_MOCK.filter((row: EntitlementRowType) =>
        row.title.toLowerCase().includes(searchValue.toLowerCase())
      ),
    [searchValue]
  );

  return (
    <Box
      className={entitlementClasses}
      dataTestId={testIds.SOD_ENTITLEMENT_TAB}>
      <Box tag='section' className={listAsideClasses}>
        {/* Main scroll area */}
        <Box className='Sod-ViolationsEntitlement-scrollArea'>
          <Box className='Sod-ViolationsEntitlement-listAndControls'>
            {/* Controls Wrapper */}
            <Box className='Sod-ViolationsEntitlement-controlsWrapper'>
              {/* Tab Title */}
              <Box className='Sod-ViolationsEntitlement-listTitleWrapper'>
                <Typography kind='h2' color={CF_NEU_BLD}>
                  {l('Entitlements with Inherent Risk')}
                </Typography>
              </Box>
              {/* Controls */}
              <Box className='Sod-ViolationsEntitlement-controls'>
                <Box className='Sod-ViolationsEntitlement-controlsLeft'>
                  <ButtonSelect
                    kind='rectangle'
                    label='Application: All'
                    onClick={() => {}}
                    isOpen={false}
                    size='medium'
                    prefixIcon={<Icon kind='Applications' />}
                    isVisualCueEnabled={false}
                    isDisabled
                  />
                  <ButtonSelect
                    kind='rectangle'
                    label={l('Filter')}
                    onClick={() => {}}
                    isOpen={false}
                    size='medium'
                    prefixIcon={<Icon kind='Filter' />}
                    isVisualCueEnabled={false}
                    isDisabled
                  />
                  <InputField
                    name='basic'
                    placeholder='Search'
                    kind='searchCollapse'
                    size='medium'
                    prefixIcon={
                      <Icon
                        kind='Search'
                        color='--color-foreground-neutral-subtle'
                      />
                    }
                    value={searchValue}
                    setValue={setSearchValue}
                    isSearchExpanded={isSearchExpanded}
                    setIsSearchExpanded={setIsSearchExpanded}
                    classNames={{
                      inputWrapper: 'Sod-ViolationsEntitlement-searchInput',
                    }}
                  />
                </Box>
                <Box className='Sod-ViolationsEntitlement-controlsRight'>
                  <ButtonIcon
                    size='large'
                    kind='outlinedSecondary'
                    icon={<Icon kind='SaviCompanion' />}
                    onClick={handleOpenInsights}
                    tabIndex={isInsightsOpen ? -1 : 0}
                    ariaLabel='Open Risk Insights Aside'
                    className='Sod-ViolationsEntitlement-insightOpenButton'
                  />
                </Box>
              </Box>
              {/* Subheading */}
              <Box className='Sod-ViolationsEntitlement-listSubheadingWrapper'>
                <Typography kind='body3' color={CF_NEU_STL}>
                  {`${filteredListRows.length} ${l('Entitlements')}`}
                </Typography>
                <Box className='Sod-ViolationsEntitlement-sortViewGroup'>
                  <ButtonSelect
                    kind='rectangle'
                    color='transparent'
                    label={l('Sort')}
                    onClick={() => {}}
                    isOpen={false}
                    size='small'
                    prefixIcon={<Icon kind='Sort' />}
                    isVisualCueEnabled={false}
                  />
                  <span className='Sod-ViolationsEntitlement-buttonDividerLine' />
                  <SodColumnsList columns={[]} />
                </Box>
              </Box>
            </Box>
            {/* List */}
            <SodEntitlementListGroup
              handleOpenSidePanel={handleOpenSidePanel}
              selectedRow={selectedRow}
              listRows={filteredListRows}
            />
          </Box>
        </Box>
        {/* Entitlement Asides */}
        <Box className='Sod-ViolationsEntitlement-asides'>
          {activeAside === ACTIVE_ASIDE_OPTIONS.INSIGHTS ? (
            <Box className='Sod-ViolationsEntitlement-insightsWrapper'>
              {/* This needs revamp */}
              {/* <SodInsights
                isInsightsOpen={isInsightsOpen}
                setIsInsightsOpen={setIsInsightsOpen}
              /> */}
            </Box>
          ) : (
            <SodEntitlementSidePanel
              isSidePanelOpen={isSidePanelOpen}
              handleCloseSidePanel={handleCloseSidePanel}
              selectedRow={selectedRow}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default memo(ViolationsEntitlement);
